import { useState } from "react";
import { Link } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { useAgentSessions } from "../../api/queries";
import type { AgentSession, AgentSessionStatus } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { AgentSessionStatusPill, WorktreeDirtyPill } from "../../components/StatePill";
import { RunTaskDialog } from "./RunTaskDialog";
import { PromoteSessionDialog } from "./PromoteSessionDialog";
import { StartBonnieSessionDialog } from "./StartBonnieSessionDialog";
import "./bonnie.css";

/** Exported for reuse by `DevelopSessionPage`, which renders the same live
 * session-status signal on a single session rather than a table row — one
 * shared pill (`AgentSessionStatusPill`), not a second hand-copied badge. */
export function StatusBadge({ status }: { status: AgentSessionStatus }) {
  return <AgentSessionStatusPill status={status} />;
}

/** Renders the worktree's dirty/clean state via the shared `StatePill`
 * system (UX polish wave 3 — was a bespoke `.bonnie-dirty` pill; see
 * `WorktreeDirtyPill`'s doc comment for the tone rationale). Exported for
 * reuse by `DevelopSessionPage` — see `StatusBadge`'s doc comment. */
export function DirtyBadge({ session }: { session: AgentSession }) {
  return <WorktreeDirtyPill dirty={session.dirty} />;
}

export function BonnieSessionsPage() {
  const { session } = useAuth();
  const paginator = usePaginator();
  const { data, isPending, isError, error, refetch } = useAgentSessions({
    cursor: paginator.cursor,
    local: true,
  });

  // `local: true` above is a REQUEST, not a guarantee (P6-core, #369, has no
  // server-side support for it yet — the server ignores the param and
  // returns every session, hosted included). Filter client-side too so this
  // dashboard is correct regardless of whether the server honors the filter
  // — mirrors AgentSessionsPage's own client-side exclusion of Bonnie rows,
  // the other half of keeping the two dashboards' resource sets disjoint.
  // Safe to drop once the server-side filter lands and is confirmed.
  const bonnieItems = data ? data.items.filter((s) => s.local === true) : [];

  const [startOpen, setStartOpen] = useState(false);
  const [taskTarget, setTaskTarget] = useState<AgentSession | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<AgentSession | null>(null);

  const canManage = canManageFleet(session?.role);

  const startButton = (
    <Button variant="primary" onClick={() => setStartOpen(true)}>
      Start session
    </Button>
  );

  // Reverse of AgentSessionsPage's own "Local sessions (Bonnie) →" link — the
  // two dashboards cover disjoint resource sets (see bonnieItems' comment
  // above) and neither linked to the other before this.
  const headerActions = (
    <>
      <Link to="/agent-sessions" className="button button--secondary">
        Hosted sessions →
      </Link>
      {canManage && startButton}
    </>
  );

  const columns: DataTableColumn<AgentSession>[] = [
    { key: "label", header: "Session", cell: (s) => s.label, className: "data-table__primary" },
    { key: "status", header: "Status", cell: (s) => <StatusBadge status={s.status} /> },
    {
      key: "branch",
      header: "Branch",
      cell: (s) => (s.branch ? <code className="bonnie-branch">{s.branch}</code> : "—"),
      className: "data-table__secondary",
    },
    { key: "dirty", header: "Worktree", cell: (s) => <DirtyBadge session={s} /> },
    {
      key: "changeSummary",
      header: "What changed",
      cell: (s) => s.changeSummary || (s.dirty === false ? "No changes" : "—"),
      className: "data-table__secondary",
    },
    {
      key: "node",
      header: "Machine",
      cell: (s) => s.node || s.nodeId || "—",
      className: "data-table__secondary",
    },
    {
      key: "createdBy",
      header: "Started by",
      cell: (s) => s.createdBy || "—",
      className: "data-table__secondary",
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: <span className="visually-hidden">Actions</span>,
            cell: (s: AgentSession) => (
              <span style={{ display: "flex", gap: "var(--space-2)" }}>
                <Button variant="secondary" onClick={() => setTaskTarget(s)}>
                  Run task
                </Button>
                <Button variant="secondary" onClick={() => setPromoteTarget(s)}>
                  Promote
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Bonnie sessions"
        description="Governed AI coding agents working your own code, locally, on their own git worktree — see which agent is on which branch and what it's changed, then drive it: send it a task, or promote its work to a branch/PR."
        actions={headerActions}
      />

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading Bonnie sessions…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error
            ? error.message
            : "Couldn't load Bonnie sessions. Check your connection and try again."
        }
        isEmpty={Boolean(data && bonnieItems.length === 0)}
        empty={
          <EmptyState
            title="No Bonnie sessions"
            description="Start one from the comebye bonnie CLI on your machine, or right here."
            action={canManage ? startButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable columns={columns} rows={bonnieItems} rowKey={(s) => s.id} />
            <PaginatorControls
              itemCount={bonnieItems.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="sessions"
            />
          </>
        )}
      </QueryStates>

      {canManage && (
        <StartBonnieSessionDialog open={startOpen} onClose={() => setStartOpen(false)} />
      )}

      {canManage && (
        <RunTaskDialog session={taskTarget} onClose={() => setTaskTarget(null)} />
      )}

      {canManage && (
        <PromoteSessionDialog session={promoteTarget} onClose={() => setPromoteTarget(null)} />
      )}
    </>
  );
}
